import type { ApexOptions } from 'apexcharts'

/**
 * Ponto do gráfico; `valor` `null` é lacuna, nunca zero. `dataFim` existe quando o ponto
 * agrupa vários dias (semana): `data` é o primeiro dia dentro do período.
 */
export type PontoSerie = {
  data: string
  dataFim?: string
  valor: number | null
  n: number
}

export type CoresSerieDiaria = {
  serie: string
  texto: string
  grade: string
  fundoRotulo: string
  referencia: string
}

export type EntradaOpcoesSerieDiaria = {
  pontos: PontoSerie[]
  nomeSerie: string
  cores: CoresSerieDiaria
  formatarValor: (valor: number) => string
  formatarEixoY: (valor: number) => string
  formatarRotuloDia: (data: string) => string
  /** Rótulo de um ponto agrupado ("dd/MM a dd/MM") no tooltip. */
  formatarIntervalo?: (dataInicio: string, dataFim: string) => string
  textoSemDados: string
  maximoY?: number
  /** Valor de comparação (média do período anterior): linha tracejada, sem rótulo no SVG. */
  valorReferencia?: number | null
  altura?: number
  /** Largura do contêiner em px; define quantos rótulos do eixo X cabem sem sobrepor. */
  larguraGrafico?: number
}

const ALTURA_PADRAO = 240
// Folga acima do maior valor (série ou referência), para a linha não colar no topo.
const FOLGA_EIXO_Y = 1.1
const LARGURA_PADRAO = 350
// Espaço do eixo Y e das margens, que não sobra para os rótulos do eixo X.
const LARGURA_FORA_DA_AREA_UTIL = 60
// "dd/MM" em 12px ocupa ~36px; o resto é folga entre dois rótulos vizinhos.
const LARGURA_MINIMA_POR_ROTULO = 56
const MIN_ROTULOS_EIXO_X = 2

/** Índice do último ponto com valor; -1 quando a série não tem dado. */
const indiceUltimoComDado = (pontos: PontoSerie[]): number => {
  for (let i = pontos.length - 1; i >= 0; i--) {
    if (pontos[i].valor !== null) return i
  }
  return -1
}

/** De quantos em quantos dias o eixo X mostra um rótulo, para caber na largura sem sobrepor. */
export const calcularPassoRotulosEixoX = (
  totalPontos: number,
  larguraGrafico: number = LARGURA_PADRAO
): number => {
  const larguraUtil = Math.max(larguraGrafico - LARGURA_FORA_DA_AREA_UTIL, 0)
  const maximoRotulos = Math.max(
    Math.floor(larguraUtil / LARGURA_MINIMA_POR_ROTULO),
    MIN_ROTULOS_EIXO_X
  )
  return Math.max(Math.ceil(totalPontos / maximoRotulos), 1)
}

/**
 * Índices que recebem rótulo no eixo X: um a cada `passo`, ancorados no último ponto com
 * dado, para que o dia anotado sempre tenha a data visível.
 */
export const indicesRotulosEixoX = (
  totalPontos: number,
  ancora: number,
  passo: number
): number[] => {
  const base = ancora >= 0 ? ancora : totalPontos - 1
  const indices: number[] = []
  for (let i = 0; i < totalPontos; i++) {
    if ((((i - base) % passo) + passo) % passo === 0) indices.push(i)
  }
  return indices
}

/** Rótulo de tooltip/tabela do ponto: o dia, ou "dd/MM a dd/MM" quando agrupa dias. */
export const rotuloDoPonto = (
  ponto: { data: string; dataFim?: string },
  formatarRotuloDia: (data: string) => string,
  formatarIntervalo?: (dataInicio: string, dataFim: string) => string
): string =>
  ponto.dataFim && ponto.dataFim !== ponto.data && formatarIntervalo
    ? formatarIntervalo(ponto.data, ponto.dataFim)
    : formatarRotuloDia(ponto.data)

/**
 * Opções da ApexCharts para uma série em linha. Sem import de runtime da biblioteca,
 * para ser testável em jsdom. Ponto sem dado continua `null` (lacuna), só o último
 * ponto com dado recebe rótulo, e a referência vira uma linha tracejada.
 */
export const montarOpcoesSerieDiaria = ({
  pontos,
  nomeSerie,
  cores,
  formatarValor,
  formatarEixoY,
  formatarRotuloDia,
  formatarIntervalo,
  textoSemDados,
  maximoY,
  valorReferencia = null,
  altura,
  larguraGrafico,
}: EntradaOpcoesSerieDiaria): ApexOptions => {
  const categorias = pontos.map((ponto) => formatarRotuloDia(ponto.data))
  const ultimo = indiceUltimoComDado(pontos)
  const valorUltimo = ultimo >= 0 ? pontos[ultimo].valor : null

  const passoRotulos = calcularPassoRotulosEixoX(pontos.length, larguraGrafico)
  const rotulosVisiveis = new Set(
    indicesRotulosEixoX(pontos.length, ultimo, passoRotulos).map((i) => categorias[i])
  )

  // Na metade direita o rótulo cresce para a esquerda do ponto, senão é cortado na borda.
  const rotuloParaEsquerda = ultimo >= pontos.length / 2

  const anotacoes: NonNullable<NonNullable<ApexOptions['annotations']>['points']> =
    valorUltimo !== null
      ? [
          {
            x: categorias[ultimo],
            y: valorUltimo,
            marker: { size: 4, fillColor: cores.serie, strokeColor: cores.serie },
            label: {
              text: formatarValor(valorUltimo),
              borderColor: cores.serie,
              textAnchor: rotuloParaEsquerda ? 'end' : 'start',
              offsetX: rotuloParaEsquerda ? -6 : 6,
              style: { background: cores.fundoRotulo, color: cores.texto, fontWeight: 600 },
            },
          },
        ]
      : []

  // A anotação não estende o eixo: o máximo considera a referência quando ela passa da série.
  const maiorValor = Math.max(...pontos.map((ponto) => ponto.valor ?? 0), valorReferencia ?? 0)
  const maximoCalculado =
    maximoY ?? (valorReferencia !== null && maiorValor > 0 ? maiorValor * FOLGA_EIXO_Y : undefined)

  const referencia: NonNullable<NonNullable<ApexOptions['annotations']>['yaxis']> =
    valorReferencia !== null
      ? [
          {
            y: valorReferencia,
            borderColor: cores.referencia,
            borderWidth: 2,
            strokeDashArray: 6,
            label: { text: '' },
          },
        ]
      : []

  return {
    chart: {
      type: 'line',
      height: altura ?? ALTURA_PADRAO,
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: 'inherit',
      animations: { enabled: false },
    },
    series: [{ name: nomeSerie, data: pontos.map((ponto) => ponto.valor) }],
    colors: [cores.serie],
    stroke: { curve: 'straight', width: 2 },
    markers: { size: 3 },
    dataLabels: { enabled: false },
    xaxis: {
      categories: categorias,
      // A ApexCharts converte o eixo da linha em numérico: um tick por ponto, para os rótulos
      // caírem sobre os pontos; quem decide qual aparece é o formatter.
      tickAmount: Math.max(pontos.length - 1, 1),
      labels: {
        rotate: 0,
        hideOverlappingLabels: true,
        formatter: (valor: string) => (rotulosVisiveis.has(String(valor)) ? String(valor) : ''),
        style: { colors: cores.texto, fontSize: '12px' },
      },
      axisTicks: { show: false },
      axisBorder: { show: false },
      tooltip: { enabled: false },
    },
    yaxis: {
      min: 0,
      max: maximoCalculado,
      forceNiceScale: maximoY === undefined,
      labels: {
        formatter: (valor: number) => formatarEixoY(valor),
        style: { colors: cores.texto, fontSize: '12px' },
      },
    },
    grid: {
      borderColor: cores.grade,
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
    },
    annotations: { points: anotacoes, yaxis: referencia },
    tooltip: {
      // O formatter do eixo esconde datas; o tooltip mostra sempre a do ponto apontado.
      x: {
        formatter: (valor: number, opcoes?: { dataPointIndex?: number }) => {
          const ponto = pontos[opcoes?.dataPointIndex ?? -1]
          return ponto ? rotuloDoPonto(ponto, formatarRotuloDia, formatarIntervalo) : String(valor)
        },
      },
      y: { formatter: (valor: number) => formatarValor(valor) },
    },
    legend: { show: false },
    noData: {
      text: textoSemDados,
      align: 'center',
      verticalAlign: 'middle',
      style: { color: cores.texto, fontSize: '14px' },
    },
  }
}
